import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading2,
  Heading3,
  Heading4,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Sigma,
  Underline as UnderlineIcon,
  type LucideIcon,
} from 'lucide-react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import StarterKit from '@tiptap/starter-kit';

import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';

import { EquationModal } from './EquationModal';
import { LinkDialog } from './LinkDialog';
import styles from './TextUnitEditor.module.css';

interface TextUnitEditorProps {
  /**
   * What the text is, said by screen readers: "Hint", "Part A prompt". Every
   * editor on a page is a text box, so without it none can be told apart.
   */
  label: string;
  onChange: (html: string) => void;
  /** What goes here, shown while the editor is empty. */
  placeholder: string;
  value?: string;
}

type ToolbarGroup = 'alignment' | 'blocks' | 'format' | 'headings' | 'insert';

interface ToolbarButton {
  action: () => void;
  group: ToolbarGroup;
  icon: LucideIcon;
  isActive?: boolean;
  isToggle?: boolean;
  label: string;
  showText?: boolean;
}

function getWrappedIndex(index: number, total: number) {
  if (total === 0) {
    return 0;
  }

  if (index < 0) {
    return total - 1;
  }

  if (index >= total) {
    return 0;
  }

  return index;
}

export function TextUnitEditor({
  label,
  onChange,
  placeholder,
  value = '<p></p>',
}: TextUnitEditorProps) {
  const [isEquationModalOpen, setIsEquationModalOpen] = useState(false);
  // The address of the link under the selection when the dialog opened, or
  // '' for a new one; null while the dialog is closed.
  const [linkDialogHref, setLinkDialogHref] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const toolbarButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Read by the placeholder extension, which is configured once.
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;

  const editor = useEditor({
    content: value,
    editorProps: {
      // These replace TipTap's own attributes, so the role it adds is
      // repeated here.
      attributes: {
        'aria-label': label,
        'aria-multiline': 'true',
        'aria-placeholder': placeholder,
        role: 'textbox',
      },
    },
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4],
        },
        link: {
          autolink: false,
          openOnClick: false,
        },
        underline: {},
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image,
      Placeholder.configure({
        placeholder: () => placeholderRef.current,
      }),
    ],
    immediatelyRender: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
  });

  useEditorState({
    editor,
    selector: ({ transactionNumber }) => transactionNumber,
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentHtml = editor.getHTML();

    if (currentHtml !== value) {
      editor.commands.setContent(value, {
        emitUpdate: false,
        parseOptions: {
          preserveWhitespace: 'full',
        },
      });
    }
  }, [editor, value]);

  if (!editor) {
    return <div className={styles.loading}>Preparing rich text editor…</div>;
  }

  const getFocusedChain = () =>
    editor.chain().focus(undefined, { scrollIntoView: false });

  const toolbarButtons: ToolbarButton[] = [
    {
      action: () => getFocusedChain().toggleBold().run(),
      group: 'format',
      icon: Bold,
      isActive: editor.isActive('bold'),
      isToggle: true,
      label: 'Bold',
    },
    {
      action: () => getFocusedChain().toggleItalic().run(),
      group: 'format',
      icon: Italic,
      isActive: editor.isActive('italic'),
      isToggle: true,
      label: 'Italic',
    },
    {
      action: () => getFocusedChain().toggleUnderline().run(),
      group: 'format',
      icon: UnderlineIcon,
      isActive: editor.isActive('underline'),
      isToggle: true,
      label: 'Underline',
    },
    {
      action: () => getFocusedChain().toggleHeading({ level: 2 }).run(),
      group: 'headings',
      icon: Heading2,
      isActive: editor.isActive('heading', { level: 2 }),
      isToggle: true,
      label: 'Heading 2',
    },
    {
      action: () => getFocusedChain().toggleHeading({ level: 3 }).run(),
      group: 'headings',
      icon: Heading3,
      isActive: editor.isActive('heading', { level: 3 }),
      isToggle: true,
      label: 'Heading 3',
    },
    {
      action: () => getFocusedChain().toggleHeading({ level: 4 }).run(),
      group: 'headings',
      icon: Heading4,
      isActive: editor.isActive('heading', { level: 4 }),
      isToggle: true,
      label: 'Heading 4',
    },
    {
      action: () => getFocusedChain().toggleBulletList().run(),
      group: 'blocks',
      icon: List,
      isActive: editor.isActive('bulletList'),
      isToggle: true,
      label: 'Bullet list',
    },
    {
      action: () => getFocusedChain().toggleOrderedList().run(),
      group: 'blocks',
      icon: ListOrdered,
      isActive: editor.isActive('orderedList'),
      isToggle: true,
      label: 'Numbered list',
    },
    {
      action: () => getFocusedChain().toggleBlockquote().run(),
      group: 'blocks',
      icon: Quote,
      isActive: editor.isActive('blockquote'),
      isToggle: true,
      label: 'Block quote',
    },
    {
      action: () => getFocusedChain().toggleCode().run(),
      group: 'blocks',
      icon: Code,
      isActive: editor.isActive('code'),
      isToggle: true,
      label: 'Code',
    },
    {
      action: () => getFocusedChain().setTextAlign('left').run(),
      group: 'alignment',
      icon: AlignLeft,
      isActive: editor.isActive({ textAlign: 'left' }),
      isToggle: true,
      label: 'Align left',
    },
    {
      action: () => getFocusedChain().setTextAlign('center').run(),
      group: 'alignment',
      icon: AlignCenter,
      isActive: editor.isActive({ textAlign: 'center' }),
      isToggle: true,
      label: 'Align center',
    },
    {
      action: () => getFocusedChain().setTextAlign('right').run(),
      group: 'alignment',
      icon: AlignRight,
      isActive: editor.isActive({ textAlign: 'right' }),
      isToggle: true,
      label: 'Align right',
    },
    {
      action: () => {
        const href: unknown = editor.getAttributes('link').href;
        setLinkDialogHref(typeof href === 'string' ? href : '');
      },
      group: 'insert',
      icon: LinkIcon,
      label: 'Insert link',
    },
    {
      action: () => {
        setIsEquationModalOpen(true);
      },
      group: 'insert',
      icon: Sigma,
      label: 'Insert equation',
      showText: true,
    },
  ];

  const totalButtons = toolbarButtons.length;

  // Where focus goes when one of the toolbar's dialogs closes: back in the
  // text after a change, so the instructor can keep writing, and otherwise on
  // the button that opened it.
  function returnFocusFromDialog(changed: boolean, buttonLabel: string) {
    if (changed) {
      getFocusedChain().run();
      return;
    }

    toolbarButtonRefs.current[
      toolbarButtons.findIndex((button) => button.label === buttonLabel)
    ]?.focus();
  }

  function moveFocus(index: number) {
    const nextIndex = getWrappedIndex(index, totalButtons);
    setFocusedIndex(nextIndex);
    toolbarButtonRefs.current[nextIndex]?.focus();
  }

  function handleToolbarKeyDown(
    index: number,
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveFocus(index + 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveFocus(index - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveFocus(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveFocus(totalButtons - 1);
    }
  }

  return (
    <div className={styles.root}>
      <div aria-label="Text formatting toolbar" className={styles.toolbar} role="toolbar">
        {toolbarButtons.map((button, index) => {
          const previousButton = toolbarButtons[index - 1];
          const shouldRenderDivider =
            previousButton && previousButton.group !== button.group;
          const Icon = button.icon;

          return (
            <div className={styles.toolbarItem} key={button.label}>
              {shouldRenderDivider ? (
                <span aria-hidden="true" className={styles.toolbarDivider} />
              ) : null}

              <Tooltip content={button.label}>
                <Button
                  {...(button.isToggle
                    ? { 'aria-pressed': button.isActive ?? false }
                    : {})}
                  aria-label={button.label}
                  className={styles.toolbarButton}
                  data-active={button.isActive ? 'true' : 'false'}
                  iconOnly={!button.showText}
                  onClick={button.action}
                  onFocus={() => {
                    setFocusedIndex(index);
                  }}
                  onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
                    handleToolbarKeyDown(index, event);
                  }}
                  ref={(node) => {
                    toolbarButtonRefs.current[index] = node as HTMLButtonElement | null;
                  }}
                  size="small"
                  tabIndex={index === focusedIndex ? 0 : -1}
                  variant="ghost"
                >
                  <Icon aria-hidden="true" className={styles.toolbarIcon} size={18} />
                  {button.showText ? (
                    <span className={styles.toolbarText}>Equation</span>
                  ) : null}
                </Button>
              </Tooltip>
            </div>
          );
        })}
      </div>

      <EditorContent className={styles.editor} editor={editor} />

      {/* The dialogs change the text without focusing it: while one is open
          it holds focus, and the editor takes it back once it has gone. */}
      <EquationModal
        initialValue=""
        isOpen={isEquationModalOpen}
        onClose={() => {
          setIsEquationModalOpen(false);
        }}
        onClosed={(outcome) => {
          returnFocusFromDialog(outcome === 'inserted', 'Insert equation');
        }}
        onInsert={(latex) => {
          editor.chain().insertContent(`$$${latex}$$`).run();
          setIsEquationModalOpen(false);
        }}
      />

      <LinkDialog
        initialHref={linkDialogHref ?? undefined}
        isOpen={linkDialogHref !== null}
        onClose={() => {
          setLinkDialogHref(null);
        }}
        onClosed={(outcome) => {
          returnFocusFromDialog(outcome === 'changed', 'Insert link');
        }}
        onRemove={() => {
          editor.chain().extendMarkRange('link').unsetLink().run();
          setLinkDialogHref(null);
        }}
        onSubmit={(href) => {
          if (editor.state.selection.empty && !editor.isActive('link')) {
            // Nothing selected to link: the address goes in as the link's text.
            editor
              .chain()
              .insertContent({ marks: [{ attrs: { href }, type: 'link' }], text: href, type: 'text' })
              .run();
          } else {
            editor.chain().extendMarkRange('link').setLink({ href }).run();
          }

          setLinkDialogHref(null);
        }}
      />
    </div>
  );
}
